import { PublicRoutes } from '@/constants/routes'
import { IConfigSeller } from '@/models/configSeller'
import handLeft from '@/assets/images/login-hand-left.png'
import handRight from '@/assets/images/login-hand-right.png'
import { Link } from '@nextui-org/react'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { LoginForm } from './components/LoginForm'
import LoginPageLegacy from './legacy/LoginPageLegacy'
import { useHasRedesign } from '@/utils/redesignSellers'

function FlowFieldBackground () {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Tonos de marca Reuse V5 (reBlue/solidPurple/boldBlue + un acento verde, ver src/utils/reuseBrand.ts)
    const hues = [225, 232, 238, 102]

    interface P { x: number, y: number, hue: number, age: number, maxAge: number }
    const COUNT = 220
    const mk = (): P => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      hue: hues[Math.floor(Math.random() * hues.length)],
      age: Math.floor(Math.random() * 280),
      maxAge: 200 + Math.floor(Math.random() * 160)
    })
    const particles: P[] = Array.from({ length: COUNT }, mk)

    const SCALE = 0.0028
    const SPEED = 1.4
    let t = 0

    const tick = () => {
      t += 0.0025
      ctx.fillStyle = 'rgba(255,255,255,0.045)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      for (const p of particles) {
        const angle =
          Math.sin(p.x * SCALE + t) * Math.PI +
          Math.cos(p.y * SCALE + t * 0.65) * Math.PI +
          Math.sin((p.x - p.y) * SCALE * 0.4 + t * 0.4) * 0.6

        const vx = Math.cos(angle) * SPEED
        const vy = Math.sin(angle) * SPEED
        const life = p.age / p.maxAge
        const opacity = Math.sin(life * Math.PI) * 0.32

        ctx.strokeStyle = `hsla(${p.hue},78%,52%,${opacity})`
        ctx.lineWidth = 0.95
        ctx.beginPath()
        ctx.moveTo(p.x, p.y)
        p.x += vx
        p.y += vy
        p.age++
        ctx.lineTo(p.x, p.y)
        ctx.stroke()

        if (p.age >= p.maxAge || p.x < -4 || p.x > canvas.width + 4 ||
            p.y < -4 || p.y > canvas.height + 4) {
          Object.assign(p, mk())
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}
    />
  )
}

const LoginPageRedesign = () => {
  const configState = useSelector((store: { config: IConfigSeller }) => store.config)

  return (
    <div className='relative min-h-screen flex items-center justify-center overflow-hidden bg-white'>
      <FlowFieldBackground />

      <img
        src={handLeft}
        alt=''
        aria-hidden
        className='pointer-events-none select-none absolute left-0 top-1/2 z-[1] w-[46vw] max-w-[620px] min-w-[380px] -translate-x-[22%] -translate-y-[70%] opacity-40 mix-blend-multiply'
        style={{
          WebkitMaskImage: 'linear-gradient(135deg, transparent 0%, transparent 9%, black 22%)',
          maskImage: 'linear-gradient(135deg, transparent 0%, transparent 9%, black 22%)'
        }}
      />
      <img
        src={handRight}
        alt=''
        aria-hidden
        className='pointer-events-none select-none absolute right-0 top-1/2 z-[1] w-[46vw] max-w-[620px] min-w-[380px] translate-x-[15%] translate-y-[10%] opacity-40 mix-blend-multiply'
      />

      <div className='relative z-10 w-full max-w-sm mx-4'>
        <div className='rounded-3xl px-8 py-10 flex flex-col gap-7 border border-slate-200/80 bg-white/90 backdrop-blur-xl shadow-xl'>
          <div className='flex flex-col items-center gap-4'>
            <Link href={PublicRoutes.LOGIN} className='rounded-2xl bg-white shadow-md px-5 py-3 flex items-center justify-center'>
              <img src={configState.config.logo} alt='Logo' className='h-9 max-w-[160px] object-contain' />
            </Link>
            <div className='text-center'>
              <h1 className='text-xl font-semibold tracking-tight text-slate-800'>Trade-in</h1>
              <p className='text-sm mt-1 text-slate-400'>Inicia sesión para continuar</p>
            </div>
          </div>

          <LoginForm />
        </div>
      </div>
    </div>
  )
}

// El login nuevo solo se muestra a los sellers liberados (ver REDESIGN_SELLERS); el resto
// sigue con el login anterior.
const LoginPage = () => (useHasRedesign() ? <LoginPageRedesign /> : <LoginPageLegacy />)

export default LoginPage
